<template>
  <div>
    <div class="container-fluid bg-light py-6 mt-0">
      <div class="container text-center animated bounceInDown">
        <h1 class="display-1 mb-4">{{ $t("courses") }}</h1>
        <ol class="breadcrumb justify-content-center mb-0 animated bounceInDown">
          <li class="breadcrumb-item">
            <router-link to="/">{{ $t("home") }}</router-link>
          </li>
          <li class="breadcrumb-item">{{ $t("courses") }}</li>
          <li class="breadcrumb-item text-dark" aria-current="page">
            {{ $t(levelKey) }}
          </li>
        </ol>
      </div>
    </div>

    <div class="container-fluid course py-6">
      <div class="container">
        <div class="text-center mb-5">
          <small
            class="d-inline-block fw-bold text-dark text-uppercase bg-light border border-primary rounded-pill px-4 py-1 mb-3"
            >{{ $t("courses") }}</small
          >
          <h2 class="display-5 mb-0">{{ $t(levelKey) }}</h2>
        </div>

        <div v-if="isLoading" class="row g-4">
          <div v-for="n in 3" :key="n" class="col-md-6 col-lg-4">
            <div class="course-card h-100">
              <div class="course-card__media skeleton"></div>
              <div class="p-4">
                <div class="skeleton skeleton-line w-75 mb-3"></div>
                <div class="skeleton skeleton-line mb-2"></div>
                <div class="skeleton skeleton-line w-50"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="hasError" class="text-center py-5">
          <i class="fas fa-exclamation-triangle fa-3x text-primary mb-3"></i>
          <p class="mb-4">{{ $t("somethingWentWrong") }}</p>
          <button class="btn btn-primary px-4 py-2 rounded-pill" @click="loadCourses">
            {{ $t("tryAgain") }}
          </button>
        </div>

        <div v-else-if="!courses.length" class="text-center py-5">
          <i class="fas fa-book-open fa-3x text-primary mb-3"></i>
          <p class="mb-0">{{ $t("noCoursesYet") }}</p>
        </div>

        <div v-else class="row g-4">
          <div
            v-for="course in courses"
            :key="course.id"
            class="col-md-6 col-lg-4 wow bounceInUp"
            data-wow-delay="0.1s"
          >
            <router-link
              :to="{ name: 'courseDetail', params: { id: course.id } }"
              class="course-card h-100 d-flex flex-column text-decoration-none"
            >
              <div class="course-card__media">
                <img
                  :src="getImageUrl(course.cover_photo_url)"
                  :alt="getLocalizedTitle(course)"
                  :class="['course-card__img', { 'course-card__img--fallback': !course.cover_photo_url }]"
                  loading="lazy"
                  @error="useFallbackImage"
                />
                <span v-if="course.course_category" class="course-card__badge">
                  {{ course.course_category.name }}
                </span>
              </div>

              <div class="p-4 d-flex flex-column flex-grow-1">
                <h5 class="course-card__title mb-2">
                  {{ getLocalizedTitle(course) }}
                </h5>
                <p class="course-card__summary text-muted flex-grow-1">
                  {{ getLocalizedSummary(course) }}
                </p>
                <span class="btn btn-primary px-4 py-2 rounded-pill align-self-start mt-3">
                  {{ $t("readMore") }}
                </span>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mediaUrl } from "@/utils/mediaUrl";

const FALLBACK_IMAGE = "/img/logo.jpg";

export default {
  name: "CourseList",
  props: {
    /** i18n key for the level name, e.g. "beginnerLevel" */
    levelKey: {
      type: String,
      required: true,
    },
    /** Vuex action that loads this level, e.g. "getCourseBeginners" */
    fetchAction: {
      type: String,
      required: true,
    },
    /** Vuex getter holding this level's courses, e.g. "courseBeginners" */
    getterName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
    };
  },
  computed: {
    courses() {
      return this.$store.getters[this.getterName] || [];
    },
  },
  methods: {
    async loadCourses() {
      this.isLoading = true;
      this.hasError = false;

      try {
        await this.$store.dispatch(this.fetchAction);
      } catch (error) {
        console.error(`Failed to load courses via ${this.fetchAction}:`, error);
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    getImageUrl(imageUrl) {
      return mediaUrl(imageUrl, FALLBACK_IMAGE);
    },
    useFallbackImage(event) {
      if (event.target.src.endsWith(FALLBACK_IMAGE)) {
        return;
      }

      event.target.src = FALLBACK_IMAGE;
      event.target.classList.add("course-card__img--fallback");
    },
    getLocalizedTitle(course) {
      return course[`title_${this.$i18n.locale}`] || course.title_en;
    },
    getLocalizedSummary(course) {
      return course[`summary_${this.$i18n.locale}`] || course.summary_en;
    },
  },
  mounted() {
    this.loadCourses();
  },
};
</script>

<style scoped>
.course-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
  color: inherit;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.course-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
}

.course-card__media {
  position: relative;
  aspect-ratio: 16 / 10;
  background: #f6f6f8;
}

.course-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* The logo is a brand mark, not a photo — contain it instead of cropping. */
.course-card__img--fallback {
  object-fit: contain;
  padding: 1.5rem;
}

.course-card__badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  color: #5c1a72;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
}

.course-card__title {
  color: #5c1a72;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-card__summary {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.skeleton {
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
}

.skeleton-line {
  height: 14px;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .course-card,
  .skeleton {
    transition: none;
    animation: none;
  }
}
</style>
